// Pre-orders, their linked pickups, incoming pickups and the recycler overview.

import type { PreOrderRequest, UpdatePreOrderRequest, UpdatePreOrderStatusRequest } from '../../types/pre-order.type';
import { parseQueryParam, type DemoCtx } from '../core';
import {
  createPreOrder,
  deletePreOrder,
  fail,
  findPreOrder,
  linkPickups,
  linkedPickupIds,
  listIncoming,
  listPreOrders,
  preOrderDetail,
  preOrdersDashboard,
  requireOrganization,
  setPreOrderStatus,
  unlinkPickup,
  updatePreOrder,
} from '../data';
import { boolParam, bodyOf, ok, routes } from './helpers';

function orgIdOf(ctx: DemoCtx): string {
  const orgId = parseQueryParam(ctx, 'organization_id') ?? ctx.orgId;
  if (!orgId) fail(422, 'organization_id is required');
  return orgId;
}

export const preOrderRoutes = routes([
  [
    'get',
    '/pre_order',
    (ctx) =>
      ok(
        listPreOrders({
          organization_id: orgIdOf(ctx),
          page: parseQueryParam(ctx, 'page'),
          limit: parseQueryParam(ctx, 'limit'),
          sort_by: parseQueryParam(ctx, 'sort_by'),
          sort_pre_order: parseQueryParam(ctx, 'sort_pre_order'),
          material_category: parseQueryParam(ctx, 'material_category'),
          status: parseQueryParam(ctx, 'status'),
          start_date: parseQueryParam(ctx, 'start_date'),
          end_date: parseQueryParam(ctx, 'end_date'),
          search: parseQueryParam(ctx, 'search'),
        }),
      ),
  ],
  ['post', '/pre_order', (ctx) => ok(createPreOrder(bodyOf<PreOrderRequest>(ctx)))],
  ['get', '/pre_order/:id', ({ params }) => ok(preOrderDetail(params.id))],
  ['put', '/pre_order/:id', (ctx) => ok(updatePreOrder(ctx.params.id, bodyOf<UpdatePreOrderRequest>(ctx)))],
  [
    'patch',
    '/pre_order/:id/status',
    (ctx) => ok(setPreOrderStatus(ctx.params.id, bodyOf<UpdatePreOrderStatusRequest>(ctx).status)),
  ],
  [
    'delete',
    '/pre_order/:id',
    ({ params }) => {
      deletePreOrder(params.id);
      return ok(true);
    },
  ],
  [
    'get',
    '/pre_order/:id/pickups',
    ({ params }) => {
      if (!findPreOrder(params.id)) fail(404, 'Pre order not found');
      return ok(preOrderDetail(params.id).linked_pickups);
    },
  ],
  [
    'post',
    '/pre_order/:id/pickups',
    (ctx) => ok(linkPickups(ctx.params.id, bodyOf<{ pickup_order_ids: string[] }>(ctx).pickup_order_ids)),
  ],
  ['delete', '/pre_order/:id/pickups/:pickupId', ({ params }) => ok(unlinkPickup(params.id, params.pickupId))],
  [
    'get',
    '/pickup_order/incoming',
    (ctx) => {
      const orgId = orgIdOf(ctx);
      requireOrganization(orgId);
      return ok(
        listIncoming({
          recyclerOrgId: orgId,
          unlinkedOnly: boolParam(ctx.query.unlinked_only, false),
          materialCategory: parseQueryParam(ctx, 'material_category'),
          linkedIds: linkedPickupIds(),
          page: parseQueryParam(ctx, 'page') ?? 1,
          limit: parseQueryParam(ctx, 'limit') ?? 50,
        }),
      );
    },
  ],
  [
    'get',
    '/dashboard/recycler/:orgId/pre-orders',
    (ctx) => {
      requireOrganization(ctx.params.orgId);
      return ok(
        preOrdersDashboard(ctx.params.orgId, {
          start_date: parseQueryParam(ctx, 'start_date'),
          end_date: parseQueryParam(ctx, 'end_date'),
          offset: parseQueryParam(ctx, 'offset'),
          limit: parseQueryParam(ctx, 'limit'),
        }),
      );
    },
  ],
]);
